/** @jsxImportSource @emotion/react */
import {
  ToggleButton as MUIToggleButton,
  ToggleButtonGroup as MUIToggleButtonGroup,
} from '@mui/material';
import { MouseEvent } from 'react';
import { createStyles, Typography } from '../../';
import { type IconName } from '../../icons';
import { useTheme } from '../../ThemeProvider';
import { Icon } from '../Icon';

export interface Props {
  toggleOptions: {
    label: string;
    value: string;
    icon?: IconName;
  }[];
  selected: string;
  onChange: (option: string) => void;
}

export const ToggleButtonGroup = ({
  toggleOptions,
  onChange,
  selected,
}: Props) => {
  const styles = useStyles();
  const { spacing } = useTheme();

  const handleChange = (event: MouseEvent<HTMLElement>, option: string) => {
    event.stopPropagation();
    if (option !== null) {
      onChange(option);
    }
  };

  return (
    <MUIToggleButtonGroup
      color="primary"
      value={selected}
      exclusive
      onChange={handleChange}
      css={[
        styles.default,
        toggleOptions.length > 2 && styles.moreOptions,
        toggleOptions.length >= 4 && styles.maxOptions,
      ]}
    >
      {toggleOptions.map(({ label, value, icon }) => (
        <MUIToggleButton
          value={value}
          css={[styles.buttonDefault, styles.selected]}
          key={value}
        >
          <span css={styles.label}>
            {icon && <Icon name={icon} size={spacing.xxs} />}
            <Typography variant="bodySmallMedium">{label}</Typography>
          </span>
        </MUIToggleButton>
      ))}
    </MUIToggleButtonGroup>
  );
};

const useStyles = createStyles(({ palette, spacing, shape }) => ({
  default: {
    '&.MuiToggleButtonGroup-root': {
      height: spacing.s,
      border: `1px solid ${palette.grey.x100}`,
      background: palette.grey.x100,
      width: '100%',
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    '& svg': {
      color: palette.grey.x500,
    },
  },
  moreOptions: {
    '&.MuiToggleButtonGroup-root': {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
  },
  maxOptions: {
    '&.MuiToggleButtonGroup-root': {
      gridTemplateColumns: 'repeat(4, 1fr)',
    },
  },
  selected: {
    '&.Mui-selected': {
      color: palette.grey.x900,
      background: palette.white,
      borderRadius: shape.borderRadius,

      '& svg': {
        color: palette.grey.x900,
      },
    },
    '&.MuiToggleButtonGroup-grouped:not(:last-of-type)': {
      borderTopRightRadius: `calc(${shape.borderRadius}/2)`,
      borderBottomRightRadius: `calc(${shape.borderRadius}/2)`,
    },
    '&.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
      borderTopLeftRadius: `calc(${shape.borderRadius}/2)`,
      borderBottomLeftRadius: `calc(${shape.borderRadius}/2)`,
    },
    '&.Mui-selected:hover': {
      backgroundColor: palette.white,
    },
  },
  buttonDefault: {
    '&.MuiToggleButton-root': {
      border: 'none',
      padding: `calc(${spacing.xxxs}/2) ${spacing.xxxs}`,
      textTransform: 'none',
      color: palette.grey.x500,
    },
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xxxs,
  },
}));
